/**
 * PRA-001 and PRA-004. After the magic link, the practitioner names their clinic and records a
 * licence. Lattice staff verify the licence before the clinic can submit formulas.
 */
import * as stylex from "@stylexjs/stylex";
import { useState } from "react";
import type { ClinicSnapshot } from "@lattice/core";
import { LICENCE_STATES } from "@lattice/core";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Banner } from "@lattice/ui/Banner";
import { Button } from "@lattice/ui/Button";
import { focusFirstError } from "@lattice/ui/focusFirstError";
import { Field } from "@lattice/ui/Field";
import { Input } from "@lattice/ui/Input";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Select } from "@lattice/ui/Select";
import { registerClinic } from "../api.ts";
import { LeaveGuard } from "./LeaveGuard.tsx";
import { ClinicShell } from "./Shell.tsx";
/** What the server refused, and the field it is about, or null for the form as a whole. */
interface Refusal {
  readonly field: string | null;
  readonly text: string;
  /** The text sends the person to support, so the screen offers the way there. */
  readonly support?: boolean;
}

/* Each refusal about one field shows under that field. The rest show above the button. */
const FIELD_ERRORS = new Map([["invalid_name", {
  field: "clinic-name",
  text: "Enter the clinic's name."
}], ["invalid_display_name", {
  field: "display-name",
  text: "Enter your name."
}], ["invalid_licence_number", {
  field: "licence-number",
  text: "Enter a licence number using letters, digits or hyphens."
}], ["invalid_licence_state", {
  field: "licence-state",
  text: "Choose the state on the licence."
}]]);
const ERRORS = new Map([["already_registered", "This account already has a clinic."], ["staff_account", "A Lattice staff account can't register a clinic. Use the admin app instead."], ["verify_email_first", "Confirm the link we emailed before registering a clinic."], ["unreachable", "We couldn't reach Lattice. Check your connection and try again."], ["organization_failed", "We couldn't create the clinic. Try again in a minute. If it keeps happening, write to support."]]);
export function Register({
  onRegistered
}: {
  readonly onRegistered: (clinic: ClinicSnapshot) => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [name, setName] = useState("");
  const [licenceNumber, setLicenceNumber] = useState("");
  const [licenceState, setLicenceState] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Refusal | null>(null);
  const [tried, setTried] = useState(false);
  const [registered, setRegistered] = useState(false);

  /* Checked on submit and again as the person types, so an error clears once it is fixed. */
  const blank = (value: string, message: string) => tried && value.trim() === "" ? message : null;
  const fieldError = (field: string) => error?.field === field ? error.text : null;
  async function submit() {
    setBusy(true);
    setError(null);
    const result = await registerClinic({
      displayName,
      name,
      licenceNumber,
      licenceState
    }).catch(() => ({
      status: "error",
      code: "unreachable"
    }) as const);
    setBusy(false);
    if (result.status === "ok") {
      setRegistered(true);
      onRegistered(result.clinic);
    } else setError(FIELD_ERRORS.get(result.code) ?? {
      field: null,
      text: ERRORS.get(result.code) ?? "We couldn't register the clinic. Try again.",
      support: result.code === "organization_failed"
    });
  }
  return <ClinicShell title="Register your clinic" description="Lattice staff verify your licence before the clinic can submit formulas." signedIn width="narrow">
      <LeaveGuard dirty={!registered && [displayName, name, licenceNumber].some(value => value.trim() !== "")} what="the clinic you started registering" />
      <form noValidate className="x78zum5 xdt5ytf x1r0x45p xh8yej3" onSubmit={event => {
      event.preventDefault();
      setTried(true);
      const filled = [displayName, name, licenceNumber, licenceState].every(value => value.trim() !== "");
      if (!filled) focusFirstError(event.currentTarget);else if (!busy) void submit();
    }}>
        <Field label="Your name" id="display-name" error={blank(displayName, "Enter your name.") ?? fieldError("display-name")}>
          {control => <Input {...control} name="name" size="lg" autoComplete="name" value={displayName} onChange={setDisplayName} />}
        </Field>
        <Field label="Clinic name" id="clinic-name" error={blank(name, "Enter the clinic's name.") ?? fieldError("clinic-name")}>
          {control => <Input {...control} name="organization" size="lg" autoComplete="organization" value={name} onChange={setName} />}
        </Field>
        <div className="xrvj5dj x1y6fwsi x1r0x45p">
          <Field label="Licence number" id="licence-number" hint="Letters, digits and hyphens, as printed on the licence." error={blank(licenceNumber, "Enter the licence number.") ?? fieldError("licence-number")}>
            {control => <Input {...control} name="licence-number" size="lg" value={licenceNumber} onChange={setLicenceNumber} />}
          </Field>
          <Field label="State that issued it" id="licence-state" error={blank(licenceState, "Choose the state that issued the licence.") ?? fieldError("licence-state")}>
            {control => <Select {...control} name="licence-state" size="lg" placeholder="Choose" options={LICENCE_STATES.map(state => ({
            value: state,
            label: state
          }))} value={licenceState} onChange={setLicenceState} />}
          </Field>
        </div>
        {error === null || error.field !== null ? null : <Banner tone="danger" title="Not registered">
            {error.text}
          </Banner>}
        {error?.support === true ? <LinkButton href="/support" tone="quiet">
            Contact support
          </LinkButton> : null}
        <Button type="submit" testId="register-clinic" loading={busy} fullWidth>
          Register clinic
        </Button>
      </form>
    </ClinicShell>;
}